/**
 * DirectionalLightComponent - simulates a far away light source (e.g., sun).
 * Manages a THREE.DirectionalLight with target position and orthographic shadow camera.
 */
import * as THREE from 'three';
import { CSM } from 'three/addons/csm/CSM.js';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { LightComponent } from './LightComponent.js';
import { WorldSpaceCSMShadowNode } from './WorldSpaceCSMShadowNode.js';

import type { LightComponentOptions } from './LightComponent.js';
import type { IGenesysRenderer } from '../../render/IGenesysRenderer.js';


export interface DirectionalLightComponentOptions extends LightComponentOptions, DirectionalLightCascadeShadowOptions {
  /** Enable CSM shadows for this directional light */
  useCsmShadows?: boolean;
  /** Shadow camera left bound (enabled only if useCsmShadows is false) */
  shadowCameraLeft?: number;
  /** Shadow camera right bound (enabled only if useCsmShadows is false) */
  shadowCameraRight?: number;
  /** Shadow camera top bound (enabled only if useCsmShadows is false) */
  shadowCameraTop?: number;
  /** Shadow camera bottom bound (enabled only if useCsmShadows is false) */
  shadowCameraBottom?: number;
  /** Is this a sun light? */
  isSunLight?: boolean;
}

export interface DirectionalLightCascadeShadowOptions {
  csmCascadeCount?: number;
  csmLightMargin?: number;
  csmMaxFar?: number;
  csmMode?: 'practical' | 'uniform' | 'logarithmic';
  csmFade?: boolean;
}

/** Wraps a THREE.DirectionalLight. */
@EngineClass('DirectionalLightComponent')
export class DirectionalLightComponent extends LightComponent<THREE.DirectionalLight, DirectionalLightComponentOptions> {
  // Backing fields
  private _shadowCameraLeft: number = -50;
  private _shadowCameraRight: number = 50;
  private _shadowCameraTop: number = 50;
  private _shadowCameraBottom: number = -50;
  private _useCsmShadows: boolean = false;
  private _csmCascadeCount: number = 4;
  private _csmMaxFar: number = 1000;
  private _csmMode: 'practical' | 'uniform' | 'logarithmic' = 'practical';
  private _csmLightMargin: number = 100;
  private _csmFade: boolean = false;
  private _isSunLight: boolean = false;

  private csmShadowNode: CSMShadowNode | null = null;
  private csmWebGL: CSM | null = null;

  @property({ description: 'Left bound of the default orthographic shadow frustum' })
  get shadowCameraLeft(): number {
    return this._shadowCameraLeft;
  }
  set shadowCameraLeft(value: number) {
    this._shadowCameraLeft = value;
    this.configureShadowCamera({ left: value });
  }

  @property({ description: 'Right bound of the default orthographic shadow frustum' })
  get shadowCameraRight(): number {
    return this._shadowCameraRight;
  }
  set shadowCameraRight(value: number) {
    this._shadowCameraRight = value;
    this.configureShadowCamera({ right: value });
  }

  @property({ description: 'Top bound of the default orthographic shadow frustum' })
  get shadowCameraTop(): number {
    return this._shadowCameraTop;
  }
  set shadowCameraTop(value: number) {
    this._shadowCameraTop = value;
    this.configureShadowCamera({ top: value });
  }

  @property({ description: 'Bottom bound of the default orthographic shadow frustum' })
  get shadowCameraBottom(): number {
    return this._shadowCameraBottom;
  }
  set shadowCameraBottom(value: number) {
    this._shadowCameraBottom = value;
    this.configureShadowCamera({ bottom: value });
  }

  @property({ description: 'Use standard shadows for smaller/fixed scenes. Enable CSM for large view distances when you need better distant shadow detail at higher GPU cost. Only one directional light can drive CSM at a time.' })
  get useCsmShadows(): boolean {
    return this._useCsmShadows;
  }
  set useCsmShadows(value: boolean) {
    this._useCsmShadows = value;

    if (value) {
      this.ensureShadowFarCoversCsmMaxFar();
    } else {
      this.updateShadowCamera();
    }
  }

  @property({ description: 'Is this a sun light?' })
  get isSunLight(): boolean {
    return this._isSunLight;
  }
  set isSunLight(value: boolean) {
    this._isSunLight = value;
  }

  @property({ description: 'Number of shadow cascades to render (higher costs more)' })
  get csmCascadeCount(): number {
    return this._csmCascadeCount;
  }
  set csmCascadeCount(value: number) {
    const nextCount = Math.max(1, Math.round(value));
    if (nextCount === this._csmCascadeCount) return;
    this._csmCascadeCount = nextCount;
    this.configureCascadeShadows({ csmCascadeCount: nextCount });
  }

  @property({ description: 'Maximum camera distance covered by cascaded shadows (should be smaller than shadowFar)' })
  get csmMaxFar(): number {
    return this._csmMaxFar;
  }
  set csmMaxFar(value: number) {
    if (value === this._csmMaxFar) return;
    this._csmMaxFar = value;
    this.configureCascadeShadows({ csmMaxFar: value });
  }

  @property({ description: 'Cascade split distribution mode' })
  get csmMode(): 'practical' | 'uniform' | 'logarithmic' {
    return this._csmMode;
  }
  set csmMode(value: 'practical' | 'uniform' | 'logarithmic') {
    if (value === this._csmMode) return;
    this._csmMode = value;
    this.configureCascadeShadows({ csmMode: value });
  }

  @property({ description: 'Extra margin applied when fitting cascade shadow bounds' })
  get csmLightMargin(): number {
    return this._csmLightMargin;
  }
  set csmLightMargin(value: number) {
    if (value === this._csmLightMargin) return;
    this._csmLightMargin = value;
    this.configureCascadeShadows({ csmLightMargin: value });
  }

  @property({ description: 'Blend shadow transitions between cascades and near the CSM cutoff' })
  get csmFade(): boolean {
    return this._csmFade;
  }
  set csmFade(value: boolean) {
    if (value === this._csmFade) return;
    this._csmFade = value;
    this.configureCascadeShadows({ csmFade: value });
  }

  protected override createLight(): THREE.DirectionalLight {
    return new THREE.DirectionalLight();
  }

  public override initialize(options?: DirectionalLightComponentOptions): void {
    super.initialize(options);
    this.shadowCameraLeft = options?.shadowCameraLeft ?? this.shadowCameraLeft;
    this.shadowCameraRight = options?.shadowCameraRight ?? this.shadowCameraRight;
    this.shadowCameraTop = options?.shadowCameraTop ?? this.shadowCameraTop;
    this.shadowCameraBottom = options?.shadowCameraBottom ?? this.shadowCameraBottom;
    this.useCsmShadows = options?.useCsmShadows ?? this.useCsmShadows;
    this.csmCascadeCount = options?.csmCascadeCount ?? this.csmCascadeCount;
    this.csmMaxFar = options?.csmMaxFar ?? this.csmMaxFar;
    this.csmMode = options?.csmMode ?? this.csmMode;
    this.csmLightMargin = options?.csmLightMargin ?? this.csmLightMargin;
    this.csmFade = options?.csmFade ?? this.csmFade;
    this.isSunLight = options?.isSunLight ?? this.isSunLight;
    this.updateShadowCamera();
  }

  public override postLoad(): void {
    super.postLoad();
    this.updateShadowCamera();
  }

  /**
   * Move the light to a new target position while keeping direction constant.
   * @param newTargetPosition - The new target position
   */
  public moveTargetTo(newTargetPosition: THREE.Vector3): this {
    if (!this.light || !this.light.target) {
      return this;
    }
    const delta = newTargetPosition.clone().sub(this.light.target.getWorldPosition());
    this.addWorldPosition(delta);
    return this;
  }

  /**
   * Returns a unique key representing the current CSM configuration state.
   * Used to detect when CSM needs re-activation due to cascade count changes.
   * @returns JSON string of the current CSM state configuration
   */
  public getCSMStateKey(): string {
    return JSON.stringify({
      csmCascadeCount: this.csmCascadeCount
    });
  }

  /**
   * Activates Cascaded Shadow Maps (CSM) for this directional light.
   * Creates the appropriate CSM implementation (WebGPU or WebGL) based on the renderer.
   * @param renderer - The Genesys renderer instance
   * @param camera - The camera to use for CSM frustum calculations
   * @param scene - The scene to add CSM lights to
   */
  public activateCSM(renderer: IGenesysRenderer, camera: THREE.Camera, scene: THREE.Scene): void {
    if (renderer.asWebGPU()) {
      // web GPU
      this.disposeWebGPUCsm();
      if (!this.light) {
        return;
      }
      this.recreateLight();

      this.csmShadowNode = new WorldSpaceCSMShadowNode(this.light, {
        cascades: this.csmCascadeCount,
        maxFar: this.csmMaxFar,
        mode: this.csmMode,
        lightMargin: this.csmLightMargin
      });
      this.csmShadowNode.fade = this.csmFade;
      this.light.shadow.shadowNode = this.csmShadowNode;
    } else {
      // webGL
      this.disposeWebGLCsm();
      const lightDirection = new THREE.Vector3();
      this.getWorldDirection(lightDirection);
      this.csmWebGL = new CSM({
        camera,
        parent: scene,
        cascades: this.csmCascadeCount,
        maxFar: this.csmMaxFar,
        mode: this.csmMode,
        lightMargin: this.csmLightMargin,
        shadowMapSize: this.shadowMapSize,
        shadowBias: this.shadowBias,
        lightDirection,
        lightNear: this.shadowNear,
        lightFar: this.shadowFar
      });
      this.csmWebGL.fade = this.csmFade;
    }
  }

  /**
   * Deactivates CSM for this directional light and cleans up all CSM resources.
   * Disposes both WebGPU and WebGL CSM implementations, removes debug helpers,
   * and restores the light to non-CSM state.
   */
  public deactivateCSM(recreateLight: boolean = true): void {
    this.disposeWebGPUCsm();
    this.disposeWebGLCsm();
    if (recreateLight) {
      this.recreateLight();
    }
  }

  public tickCSM(renderer: IGenesysRenderer, camera: THREE.Camera, scene: THREE.Scene): void {
    const genericCSM = this.csmShadowNode ?? this.csmWebGL;
    const updateFrustums = (): void => {
      if (genericCSM) {
        if (genericCSM.maxFar !== this.csmMaxFar ||
          genericCSM.mode !== this.csmMode ||
          genericCSM.lightMargin !== this.csmLightMargin ||
          genericCSM.fade !== this.csmFade ||
          genericCSM.camera !== camera
        ) {
          genericCSM.maxFar = this.csmMaxFar;
          genericCSM.mode = this.csmMode;
          genericCSM.lightMargin = this.csmLightMargin;
          genericCSM.fade = this.csmFade;
          genericCSM.camera = camera;
          genericCSM.updateFrustums();
        }
      }
    };

    if (renderer.asWebGPU()) {
      // web GPU
      if (!this.csmShadowNode) {
        console.error('syncWebGPUCsm: missing CSM runtime');
        return;
      }

      // Let Three.js initialise CSMShadowNode during first shader build.
      // Setting .camera before that can skip node init and leave cascade shadow nodes unset.
      if (!this.isWebGPUCsmReady()) {
        return;
      }
      this.markWebGPUCsmRuntimeObjectsTransient();
      updateFrustums();
    } else {
      // webGL
      if (!this.csmWebGL) {
        console.error('syncWebGLCsm: missing CSM runtime');
        return;
      }

      const csm = this.csmWebGL;
      const lightDirection = new THREE.Vector3();
      this.getWorldDirection(lightDirection);
      csm.lightDirection.copy(lightDirection);
      updateFrustums();
      csm.update();
    }

    if (genericCSM) {
      for (let idx = 0; idx < genericCSM.lights.length; idx++) {
        const shadow = genericCSM.lights[idx].shadow;
        if (!shadow) {
          continue;
        }
        shadow.mapSize.width = this.shadowMapSize;
        shadow.mapSize.height = this.shadowMapSize;
        // CSMShadowNode._init() scales bias by (cascade_index + 1) so near cascades get less
        // bias and far cascades get progressively more. Preserve that convention here.
        shadow.bias = this.shadowBias * (idx + 1);
        shadow.normalBias = this.shadowNormalBias;
        shadow.radius = this.shadowRadius;

        const shadowCamera = shadow.camera;
        if (shadowCamera.near !== this.shadowNear ||
          shadowCamera.far !== this.shadowFar) {
          shadowCamera.near = this.shadowNear;
          shadowCamera.far = this.shadowFar;
          shadowCamera.updateProjectionMatrix();
        }
      }
    }
  }

  /**
   * Each cascade shadow camera uses shadowFar as its far plane. When shadowFar is smaller than
   * csmMaxFar, outer cascades clip geometry CSM is meant to cover. Enforced whenever CSM is on
   * or csmMaxFar increases at runtime.
   */
  private ensureShadowFarCoversCsmMaxFar(): void {
    if (!this.useCsmShadows || this.shadowFar >= this.csmMaxFar) {
      return;
    }

    const previous = this.shadowFar;
    const adjusted = this.csmMaxFar * 2;
    console.warn(
      `[DirectionalLightComponent] shadowFar (${previous}) is less than csmMaxFar (${this.csmMaxFar}). ` +
      `Auto-raising shadowFar to ${adjusted} so cascade shadow cameras cover the full CSM range.`
    );
    this.shadowFar = adjusted;
  }

  /**
   * Configure the orthographic shadow camera bounds.
   * @param options - Camera frustum bounds (left, right, top, bottom)
   */
  protected configureShadowCamera(options: { left?: number, right?: number, top?: number, bottom?: number } = {}): this {
    if (!this.light) {
      return this;
    }
    const camera = this.light.shadow.camera;
    if (options.left !== undefined) {
      camera.left = options.left;
    }
    if (options.right !== undefined) {
      camera.right = options.right;
    }
    if (options.top !== undefined) {
      camera.top = options.top;
    }
    if (options.bottom !== undefined) {
      camera.bottom = options.bottom;
    }

    camera.updateProjectionMatrix();

    return this;
  }

  private configureCascadeShadows(options: DirectionalLightCascadeShadowOptions = {}): this {
    const hasChanges =
      options.csmCascadeCount !== undefined ||
      options.csmMaxFar !== undefined ||
      options.csmMode !== undefined ||
      options.csmLightMargin !== undefined ||
      options.csmFade !== undefined;

    if (!hasChanges) {
      return this;
    }

    if (this.useCsmShadows) {
      this.ensureShadowFarCoversCsmMaxFar();
    }
    return this;
  }

  public getWebGLCsm(): CSM | null {
    return this.csmWebGL;
  }

  private disposeWebGPUCsm(): void {
    const csmShadowNode = this.csmShadowNode;
    this.csmShadowNode = null;
    if (this.light) {
      this.light.shadow.shadowNode = undefined;
    }
    if (csmShadowNode) {
      csmShadowNode.dispose();
    }
  }

  /**
   * Recreate the light when transitioning between CSM and non-CSM modes.
   * Required because CSMShadowNode clones light.shadow for each cascade,
   * which copies camera bounds, shadowNode reference, and internal renderer state.
   * A fresh light ensures clean shadow initialization for the new mode.
   */
  private recreateLight(): void {
    if (!this.light) {
      return;
    }
    const newLight = this.createLight();
    newLight.copy(this.light);
    this.light = newLight;
  }

  private disposeWebGLCsm(): void {
    if (!this.csmWebGL) {
      return;
    }

    this.csmWebGL.remove();
    this.csmWebGL.dispose();
    this.csmWebGL = null;
  }

  protected override createLightHelper(): THREE.Object3D | null {
    if (!this.light) {
      return null;
    }
    return new THREE.DirectionalLightHelper(this.light, 50 /** size */);
  }

  private isWebGPUCsmReady(): boolean {
    if (!this.csmShadowNode) {
      return false;
    }

    const runtime = this.csmShadowNode as any;
    return runtime.camera != null && runtime.mainFrustum != null && Array.isArray(runtime.lights) && runtime.lights.length > 0;
  }

  private markWebGPUCsmRuntimeObjectsTransient(): void {
    if (!this.csmShadowNode || !Array.isArray(this.csmShadowNode.lights)) {
      return;
    }

    for (const cascadeLight of this.csmShadowNode.lights) {
      cascadeLight.setTransient(true);
      cascadeLight.target.setTransient(true);
    }
  }

  private updateShadowCamera() {
    this.configureShadowCamera({
      left: this.shadowCameraLeft,
      right: this.shadowCameraRight,
      top: this.shadowCameraTop,
      bottom: this.shadowCameraBottom
    });
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override endPlay(): void {
    this.deactivateCSM(false);
    super.endPlay();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Directional';
  }
}
